import TooltipIcon from '@assets/images/icons/tooltip-arrow.svg?react'
import type { Placement } from '@popperjs/core/lib/enums'
import cx from 'classnames'
import type React from 'react'
import { type ReactNode, useEffect, useState } from 'react'
import { type StrictModifier, usePopper } from 'react-popper'

import css from './NewTooltip.module.scss'

type Props = {
  content: ReactNode
  renderEl: (
    onSetRef: (
      value:
        | ((prevState: HTMLElement | null) => HTMLElement | null)
        | HTMLElement
        | null
    ) => void,
    onMouseEnter: () => void,
    onMouseLeave: () => void,
    isOpen: boolean
  ) => ReactNode
  position?: Placement
  className?: string
  offset?: {
    x?: number
    y?: number
  }
  withArrow?: boolean
}

const NewTooltip: React.FC<Props> = ({
  content,
  renderEl,
  position,
  className,
  offset,
  withArrow,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modifiers, setModifiers] = useState<StrictModifier[]>([])
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null)
  const [targetRef, setTargetRef] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const x = offset?.x !== undefined ? offset.x : 0
    const y = offset?.y !== undefined ? offset.y : 8

    setModifiers((prevState) => {
      return [
        ...prevState,
        {
          name: 'offset',
          options: {
            offset: [x, y],
          },
        },
      ]
    })
  }, [offset])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }

    const removeOverflow = setTimeout(() => {
      document.body.style.overflow = 'auto'
    }, 1000)

    return () => clearInterval(removeOverflow)
  }, [isOpen])

  const { styles, attributes } = usePopper(targetRef, containerRef, {
    modifiers,
    placement: position || 'top',
  })

  const handleOnMouseEnter = () => {
    setIsOpen(true)
  }

  const handleOnMouseLeave = () => {
    setIsOpen(false)
  }

  return (
    <>
      {renderEl(setTargetRef, handleOnMouseEnter, handleOnMouseLeave, isOpen)}

      {isOpen && (
        <div
          ref={setContainerRef}
          style={styles.popper}
          {...attributes.popper}
          className={cx(css.container, className, {
            [css.left]: position?.includes('right'),
            [css.right]: position?.includes('left'),
            [css.top]: position?.includes('bottom'),
          })}
        >
          {withArrow && <TooltipIcon className={css.arrow} />}
          {content}
        </div>
      )}
    </>
  )
}

export default NewTooltip
